import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AppError, setUnauthenticatedHandler } from "@/api/runtime";
import { getManifest, getMe, login as apiLogin, logout as apiLogout, type Manifest, type Me } from "@/api/tools";

interface SessionState {
  me: Me | null;
  maxUploadBytes: number | undefined;
  manifest: Manifest | null;
  loading: boolean;
  can: (tool: string) => boolean;
  signIn: (username: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  returnTo: string | null;
  /** true right after an explicit sign-out (the sign-in page says so; no return route is kept) */
  signedOut: boolean;
}

const Ctx = createContext<SessionState | null>(null);

/**
 * Loading until the identity read settles and, for a signed-in person, until that person's manifest lands. A sign-in
 * after a 401 finds the manifest the unauthenticated handler settled to null: that query is a success with no data, not
 * loading, while /api/tools is in flight, and gating the landing route on it drew the not-found screen before Home and
 * gave that screen the arrival focus. A manifest that failed ends the loading, so the landing gate still refuses.
 */
export function sessionLoading(meLoading: boolean, signedIn: boolean, manifest: { data: Manifest | null | undefined; isError: boolean }): boolean {
  return meLoading || (signedIn && manifest.data == null && !manifest.isError);
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [returnTo, setReturnTo] = useState<string | null>(null);
  const [signedOut] = useState<boolean>(() => {
    try {
      const v = sessionStorage.getItem("ta.signedOut") === "1";
      sessionStorage.removeItem("ta.signedOut");
      return v;
    } catch {
      return false;
    }
  });
  const meQ = useQuery({ queryKey: ["me"], queryFn: getMe, retry: false, staleTime: 60_000 });
  const manifestQ = useQuery({ queryKey: ["manifest"], queryFn: getManifest, enabled: !!meQ.data, staleTime: 5 * 60_000 });

  useEffect(() => {
    setUnauthenticatedHandler(() => {
      setReturnTo((r) => r ?? window.location.hash);
      // every projection in memory belonged to the person whose session just ended (Codex R3-C09)
      void qc.cancelQueries();
      // Keep the identity observers attached: clearing them on a bad sign-in left a later successful login with
      // nothing to refetch. Every prior person's projection and capability data is still discarded immediately.
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" && q.queryKey[0] !== "manifest" });
      qc.setQueryData(["manifest"], null);
      qc.setQueryData(["me"], null);
    });
  }, [qc]);

  const signIn = useCallback(
    async (username: string, password: string) => {
      await apiLogin(username, password);
      // nothing cached for the previous person survives a sign-in; the identity queries themselves are refetched, not removed
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" && q.queryKey[0] !== "manifest" });
      await qc.invalidateQueries({ queryKey: ["me"] });
      await qc.invalidateQueries({ queryKey: ["manifest"] });
      if (returnTo) {
        window.location.hash = returnTo;
        setReturnTo(null);
      }
    },
    [qc, returnTo],
  );
  const signOut = useCallback(async () => {
    await apiLogout();
    qc.clear();
    try {
      sessionStorage.clear(); // drafts kept for re-authentication are discarded on an explicit sign-out (§2)
      sessionStorage.setItem("ta.signedOut", "1");
    } catch {
      /* storage unavailable */
    }
    // a full reload drops every in-memory projection of the previous person; the server has already ended the session
    window.location.replace("/");
  }, [qc]);

  const me = meQ.isError && meQ.error instanceof AppError && meQ.error.unauthenticated ? null : (meQ.data ?? null);
  const tools = useMemo(() => new Set((manifestQ.data?.tools ?? []).map((t) => t.name)), [manifestQ.data]);
  const value = useMemo<SessionState>(
    () => ({ me, maxUploadBytes: (me as (Me & { max_upload_bytes?: number }) | null)?.max_upload_bytes, manifest: manifestQ.data ?? null, loading: sessionLoading(meQ.isLoading, !!me, manifestQ), can: (t) => tools.has(t), signIn, signOut, returnTo, signedOut }),
    [me, manifestQ.data, manifestQ.isError, meQ.isLoading, tools, signIn, signOut, returnTo, signedOut],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useSession outside SessionProvider");
  return v;
}
